/**
 * Incremental Server-Sent Events parser for the chat stream. Framework-free
 * (copied to next-mapit).
 *
 * Text arrives in arbitrary chunks, so a frame — or a CRLF pair — may be split
 * anywhere; the parser buffers until a blank line ends the frame.
 */

/**
 * @typedef {Object} SseEvent
 * @property {string} event `event:` field; `"message"` when the frame has none.
 * @property {any} data `data:` lines joined with `\n`, parsed as JSON.
 */

/**
 * @typedef {Object} SseParser
 * @property {(text: string) => void} feed Push decoded text; emits every completed frame.
 * @property {() => void} flush Emit a trailing frame that never got its blank line (stream end).
 */

/**
 * Lines starting with `:` (heartbeats) and frames without `data:` emit nothing.
 * A frame whose data isn't valid JSON emits
 * `{ event: "error", data: { code: "parse", retryable: true } }` instead.
 *
 * @param {(event: SseEvent) => void} onEvent
 * @returns {SseParser}
 */
export function createSseParser(onEvent) {
  let buffer = "";
  // A chunk ending in "\r" may be the first half of a "\r\n" split across
  // chunks; hold it back so it isn't read as two line endings.
  let pendingCR = false;

  const emitFrame = (frame) => {
    let event = "message";
    const data = [];

    frame.split("\n").forEach((line) => {
      if (!line || line.startsWith(":")) return;
      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);

      if (field === "event") event = value;
      else if (field === "data") data.push(value);
      // `id:` / `retry:` are not used by this protocol.
    });

    if (!data.length) return;

    let parsed;
    try {
      parsed = JSON.parse(data.join("\n"));
    } catch {
      onEvent({ event: "error", data: { code: "parse", retryable: true } });
      return;
    }
    onEvent({ event, data: parsed });
  };

  const drain = () => {
    let end = buffer.indexOf("\n\n");
    while (end !== -1) {
      const frame = buffer.slice(0, end);
      // Advance before emitting so a throwing onEvent can't replay the frame.
      buffer = buffer.slice(end + 2);
      emitFrame(frame);
      end = buffer.indexOf("\n\n");
    }
  };

  return {
    feed(text) {
      if (!text) return;
      let chunk = pendingCR ? `\r${text}` : text;
      pendingCR = chunk.endsWith("\r");
      if (pendingCR) chunk = chunk.slice(0, -1);
      buffer += chunk.replace(/\r\n?/g, "\n");
      drain();
    },

    flush() {
      if (pendingCR) {
        pendingCR = false;
        buffer += "\n";
      }
      drain();
      const frame = buffer;
      buffer = "";
      if (frame) emitFrame(frame);
    },
  };
}
